import { ColumnDef } from '@tanstack/react-table';
import { Form as FormikForm } from 'formik';
import React, { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { TimeField } from '../../../../../components/tables/filterForms/TimeField';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetUsersQuery, User } from '../../../../../services/endpoints/people/user';
import {
  useGetLessonAssignmentsQuery,
  useTeachClassMutation
} from '../../../../../services/endpoints/schedule/lessonAssignment';
import { getCurrentUser, canScheduleForLocation } from '../../../../../services/helper';
import { Lesson, LessonAssignment } from '../../../../../types/lesson.type';
import { needSubClassesColumns } from '../../../../../utils/consts/columnDefs/classColumns';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
const defaultScope = 'relevant';

// TODO: add status = 'open' for filtering, check if assign instructor column is required
const defaultSort = {
  order: { field: 'lesson_date', order: 'asc' },
  sort: { id: 'lesson_date', desc: false }
};

export const userCanView_NeedSubstitute = (user: User | null) => {
  if (
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin ||
    user?.isInstructor
  ) {
    return true;
  }

  return false;
};

export const NeedSubstituteView = () => {
  const currentUser = getCurrentUser();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl(
    {
      scope: defaultScope,
      data: { entries: 10, page: 1, orderBy: defaultSort.order }
    },
    { omitScopeFromUrl: true }
  );
  const isGlobalUser = currentUser?.isGlobal;
  const isAdminUser = currentUser?.isAdmin;
  const { data, error, isLoading, isFetching } = useGetLessonAssignmentsQuery(filters);
  const [teachClass] = useTeachClassMutation();
  const {
    data: instructors,
    error: instructorsError,
    isLoading: instructorsLoading,
    isFetching: instructorsFetching
  } = useGetUsersQuery(
    {
      scope: 'colleagues',
      data: { entries: 1000, orderBy: { field: 'first_name', order: 'asc' } }
    },
    { skip: !isAdminUser }
  );
  const [lessons, setLessons] = useState<Lesson[]>([]);

  const canScheduleFor = (locationId?: number | string | null) =>
    canScheduleForLocation(currentUser, locationId);

  const filterFormFields: FilterField[] = [
    {
      name: 'course_title',
      label: 'Session',
      value: getFilterValue(filters.data?.filterBy, 'course_title')
    },
    {
      name: 'requesting_instructor_full_name',
      label: 'Instructor',
      value: getFilterValue(filters.data?.filterBy, 'requesting_instructor_full_name')
    },
    ...(isAdminUser
      ? [
          {
            name: 'course_day_of_week',
            label: 'Day of Week',
            value: getFilterValue(filters.data?.filterBy, 'course_day_of_week')
          }
        ]
      : []),
    {
      name: 'time_from',
      id: 'lesson_time',
      operator: 'greater_equal_than',
      element: (
        <TimeField
          name="time_from"
          label="Lesson time - From"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'lesson_time', 'greater_equal_than')
    },
    {
      name: 'time_to',
      id: 'lesson_time',
      operator: 'lesser_equal_than',
      element: (
        <TimeField
          name="time_to"
          label="To"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'lesson_time', 'lesser_equal_than')
    },
    {
      name: 'starts_from',
      id: 'lesson_date',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="starts_from"
          label="Lesson Date - From"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'lesson_date', 'greater_equal_than')
    },
    {
      name: 'starts_to',
      id: 'lesson_date',
      operator: 'lesser_equal_than',
      element: (
        <DateFilterField
          name="starts_to"
          label="To"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'lesson_date', 'lesser_equal_than')
    }
  ];

  if (currentUser?.isGlobal) {
    filterFormFields.push({
      name: 'location_id',
      type: 'number',
      label: 'Location',
      element: (
        <FormSelect
          name="location_id"
          label="Location"
          options={locationOptions}
          selected_Value={locationOptions.find(
            (option) =>
              String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
          )}
        />
      )
    });
  }

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading schedule needs', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setLessons(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  const tableValidationSchema = Yup.object({
    lesson_assignment_id: Yup.number().integer().min(1).required(),
    instructor: Yup.object({
      value: Yup.number().min(1, 'Instructor ID is required'),
      label: Yup.string().optional()
    }).optional()
  });

  const pickupCol: ColumnDef<LessonAssignment> = {
    header: '',
    accessorKey: 'blank',
    enableSorting: false,
    cell: ({ row }) => {
      const locationId = row.original.lesson?.course?.venue?.location_id;
      const showAssignControls = canScheduleFor(locationId);

      return (
        <FormikForm>
          <Stack
            gap={2}
            className="mt-n1"
          >
            {showAssignControls ? (
              <FormSelect
                name="instructor"
                label=""
                flip
                positionFixed
                options={
                  instructors?.results?.map((i: User) => ({
                    label: i.full_name ?? `${i.first_name} ${i.last_name}`,
                    value: i.id
                  })) ?? []
                }
                loadingError={instructorsError ? 'Error loading instructors' : undefined}
              />
            ) : null}
            {showAssignControls || !isGlobalUser ? (
              <SimpleButton
                type="submit"
                height={30}
                className="btn-sm text-center"
                variant="primary"
              >
                Pick Up Class
              </SimpleButton>
            ) : null}
          </Stack>
        </FormikForm>
      );
    }
  };

  const pickupClass = (
    values: { lesson_assignment_id: number; instructor?: { value: number } },
    rowOriginal: any
  ) => {
    let data = {
      id: rowOriginal.id,
      instructor_id: values.instructor?.value
    };
    teachClass(data)
      .unwrap()
      .then(() => {
        navigate('/admin/schedule/sessions/' + rowOriginal.course_id);
        addToast('The class has been picked up.', 'success');
      })
      .catch((error: any) => {
        // console.log(error);
        addToast(`Failed to pick up class: ${JSON.stringify(error)}`, 'error');
      });
  };

  const tableColumns = (
    isAdminUser
      ? needSubClassesColumns
      : needSubClassesColumns.filter((col) => col.header !== 'Day Of Week')
  ).concat(pickupCol);

  return (
    <div className="py-2">
      <SFCTable
        name={`Available Classes`}
        columns={tableColumns}
        data={lessons}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        defaultSort={defaultSort.sort}
        filters={filters?.data?.filterBy}
        indexDownloadPath="lesson-assignments"
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        rowFormProps={{
          validationSchema: tableValidationSchema,
          onSubmit: pickupClass,
          getInitialValues: (row) => ({
            lesson_assignment_id: row.original.id
          })
        }}
      />
    </div>
  );
};
